"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const DepartmentContext = createContext();

export const DepartmentProvider = ({ children }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  /* ================= FETCH ================= */
  const fetchDepartments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/departments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        setDepartments(data.data || []);
      } else {
        console.error("Fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Network error:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  /* ================= ADD ================= */
  const addDepartment = async (payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.create_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/departments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 201) {
        fetchDepartments();
        return true;
      } else {
        alert(data.message || "Add failed");
        return false;
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error");
      return false;
    }
  };

  /* ================= UPDATE ================= */
  const updateDepartment = async (id, payload) => {
    if (!id || !payload) {
      console.error("Payload or ID is undefined!");
      return false;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/departments/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        fetchDepartments();
        return true;
      } else {
        alert(data.message || "Update failed");
        return false;
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error");
      return false;
    }
  };

  /* ================= DELETE ================= */
  const deleteDepartment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/departments/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setDepartments((prev) => prev.filter((d) => d.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Delete failed");
    }
  };

  /* ================= STATUS TOGGLE ================= */
  const toggleDepartmentStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/departments/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: newStatus,
          update_audit_id: user.id,
        }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setDepartments((prev) =>
          prev.map((d) =>
            d.id === id ? { ...d, status: newStatus } : d
          )
        );
      } else {
        alert(data.message || "Status update failed");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Status update failed");
    }
  };

  return (
    <DepartmentContext.Provider
      value={{
        departments,
        loading,
        fetchDepartments,
        addDepartment,
        updateDepartment,
        deleteDepartment,
        toggleDepartmentStatus,
      }}
    >
      {children}
    </DepartmentContext.Provider>
  );
};

export const useDepartments = () => useContext(DepartmentContext);
